import * as THREE from 'three';
import { PointAttribute, PointAttributes, PointAttributeTypes } from '../../loader/PointAttributes';
import { ChunkData, CLASSIFICATION, DIFFERENCE, INTENSITY, PPCHReader, RGB, SEGMENT, SOURCE } from '../../ppcreader';

import { CLOUD_MAX_RANGE, DATA_DEPTH } from './constants';
import { PPCDataChunk, PPCHierarchyChunk } from './PPCChunk';
import { EnumDataFileType, EnumFileType, EnumHierarchyFileType } from './PPCFile';
import type { PPCPointCloudGeometryNode } from './PPCPointCloudGeometryNode';

export class PPCPointCloudGeometry {
  type: 'ppc';
  pointCloudName: string;
  ppchReader: PPCHReader;
  root!: PPCPointCloudGeometryNode;
  depth = 0;
  scale = 0;
  offset!: THREE.Vector3;
  span = 0;
  spacing = 0;
  minDifference = 0;
  maxDifference = 0;
  boundingBox!: THREE.Box3;
  tightBoundingBox!: THREE.Box3;
  boundingSphere!: THREE.Sphere;
  pointAttributes!: PointAttributes;
  supportedFileTypes: EnumFileType[] = [EnumHierarchyFileType.CHILD_INFO, EnumHierarchyFileType.TILE_3D_OFFSET, EnumDataFileType.XYZ];
  private dataChunkCache: Map<string, PPCDataChunk> = new Map();
  private hierarchyChunkCache: Map<string, PPCHierarchyChunk> = new Map();

  constructor({ name, ppchReader }: { name: string; ppchReader: PPCHReader }) {
    this.type = 'ppc';
    this.pointCloudName = name;
    this.ppchReader = ppchReader;
    this.reInitialize();
  }

  reInitialize() {
    this.dataChunkCache = new Map();

    const treeInfo = this.ppchReader.mTreeInfo;
    const transformInfo = this.ppchReader.mTransformationInfo;
    const fieldInfo = this.ppchReader.mFieldInfo;

    if (!treeInfo || !transformInfo || !fieldInfo) {
      throw new Error('PPCHReader data not initialized: missing treeInfo, transformInfo, or fieldInfo');
    }

    this.depth = treeInfo.mDepth;
    this.scale = transformInfo.mScale;
    this.offset = new THREE.Vector3(transformInfo.mDeltaX, transformInfo.mDeltaY, transformInfo.mDeltaZ);
    const size = CLOUD_MAX_RANGE / this.scale;
    this.boundingBox = new THREE.Box3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(size, size, size));
    this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());
    let min = new THREE.Vector3(treeInfo.mBoxP1.x, treeInfo.mBoxP1.y, treeInfo.mBoxP1.z);
    min = min.sub(this.offset);
    let max = new THREE.Vector3(treeInfo.mBoxP2.x, treeInfo.mBoxP2.y, treeInfo.mBoxP2.z);
    max = max.sub(this.offset);
    this.tightBoundingBox = new THREE.Box3(min, max);
    this.span = Math.pow(2, DATA_DEPTH);
    this.spacing = size / this.span;

    this.minDifference = fieldInfo.mMinDifference;
    this.maxDifference = fieldInfo.mMaxDifference;

    const attributes = new PointAttributes();
    attributes.add(PointAttribute.POSITION_CARTESIAN);
    if (fieldInfo.mFields[RGB]) {
      attributes.add(new PointAttribute('rgba', PointAttributeTypes.DATA_TYPE_UINT8, 4));
      this.supportedFileTypes.push(EnumDataFileType.RGB);
    }
    if (fieldInfo.mFields[INTENSITY]) {
      const intAttribute = new PointAttribute('intensity', PointAttributeTypes.DATA_TYPE_UINT16, 1);
      intAttribute.range[0] = fieldInfo.mMinIntensity;
      intAttribute.range[1] = fieldInfo.mMaxIntensity;
      attributes.add(intAttribute);
      this.supportedFileTypes.push(EnumDataFileType.INTENSITY);
    }
    if (fieldInfo.mFields[CLASSIFICATION]) {
      attributes.add(new PointAttribute('classification', PointAttributeTypes.DATA_TYPE_UINT8, 1));
      this.supportedFileTypes.push(EnumDataFileType.CLASSIFICATION);
    }
    if (fieldInfo.mFields[DIFFERENCE]) {
      attributes.add(new PointAttribute('difference', PointAttributeTypes.DATA_TYPE_UINT32, 1));
      this.supportedFileTypes.push(EnumDataFileType.DIFFERENCE);
    }
    if (fieldInfo.mFields[SEGMENT]) {
      attributes.add(new PointAttribute('segment', PointAttributeTypes.DATA_TYPE_UINT32, 1));
      this.supportedFileTypes.push(EnumDataFileType.SEGMENT);
    }
    if (fieldInfo.mFields[SOURCE]) {
      attributes.add(new PointAttribute('source', PointAttributeTypes.DATA_TYPE_UINT32, 1));
      this.supportedFileTypes.push(EnumDataFileType.SOURCE);
    }
    this.pointAttributes = attributes;
  }

  getDataChunk(info: ChunkData) {
    let chunk = this.dataChunkCache.get(info.mChunkId.toString());
    if (!chunk) {
      chunk = new PPCDataChunk({ info, ppcGeometry: this });
      this.dataChunkCache.set(chunk.id.toString(), chunk);
    }
    return chunk;
  }

  getHierarchyChunk(info: ChunkData) {
    let chunk = this.hierarchyChunkCache.get(info.mChunkId.toString());
    if (!chunk) {
      chunk = new PPCHierarchyChunk({ info, ppcGeometry: this });
      this.hierarchyChunkCache.set(chunk.id.toString(), chunk);
    }
    return chunk;
  }

  /**
   * Dispose of all cached chunks and free memory.
   * Call this when removing a point cloud from the scene.
   */
  dispose() {
    // Dispose all data chunks
    this.dataChunkCache.forEach((chunk) => {
      chunk.dispose();
    });
    this.dataChunkCache.clear();

    // Dispose all hierarchy chunks
    this.hierarchyChunkCache.forEach((chunk) => {
      chunk.dispose();
    });
    this.hierarchyChunkCache.clear();
  }
}
